import React, {useCallback, useEffect, useRef, useState} from 'react';
import createAxiosInstance from '../support/AxiosSupport';
import {IMAGE_ACCEPT, splitAcceptedImages, uniqueFileId} from '../hooks/usePasteImages';
import AuthImage from './AuthImage';
import Dialog from './Dialog';
import {PlusIcon, SparklesIcon, XIcon} from './icons';

/** Что сделать с новым кадром после подтверждения результата (backend ReanalysisPhotoMode). */
export type ReanalysisPhotoMode = 'KEEP' | 'APPEND' | 'REPLACE';

type PhotoSource = 'current' | 'new';

interface ItemReanalysisDialogProps {
    open: boolean;
    onClose: () => void;
    /** Вещь, характеристики которой переопределяются. */
    itemId: string;
    /** Ключи фотографий вещи: без них источник «текущие фото» недоступен. */
    itemPhotos: string[];
    /**
     * Свой гардероб. В чужом новое фото лежит под ключом инициатора и владельцу гардероба
     * недоступно, поэтому к вещи не привязывается — сервер трактует режим как `KEEP` (FR-7).
     */
    isOwnWardrobe: boolean;
    /** Заявка создана: идентификатор уходит в блок прогресса. */
    onStarted: (requestId: string) => void;
}

const MAX_COMMENT = 2000;

/**
 * Пункт радиогруппы: тач-зона ≥ 44px, тёмная тема. Выбранное состояние рисуется по состоянию
 * React, а не CSS-селектором по вложенному input — так оно читается в тестах и не зависит от
 * поддержки варианта `has-[]` в сборке Tailwind.
 */
const optionClass = (selected: boolean, disabled = false): string => [
    'flex items-start gap-3 min-h-[44px] px-3 py-2 rounded-lg border transition-colors duration-fast',
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    selected
        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
        : 'border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-400',
].join(' ');

const PHOTO_FATE_OPTIONS: {value: ReanalysisPhotoMode; label: string; hint: string}[] = [
    {value: 'APPEND', label: 'Добавить к фото вещи', hint: 'Прежние снимки останутся'},
    {value: 'REPLACE', label: 'Заменить фото вещи', hint: 'Прежние снимки будут откреплены'},
    {value: 'KEEP', label: 'Только для анализа', hint: 'Фото вещи не изменятся'},
];

/** Текст ошибки запуска: сообщение сервера, если оно есть и понятно пользователю. */
const startErrorText = (err: unknown): string => {
    const e = err as {response?: {status?: number; data?: {description?: string; message?: string}}};
    const status = e?.response?.status;
    const description = e?.response?.data?.description || e?.response?.data?.message;
    if (status === 402) {
        return description || 'Лимит ИИ-запросов исчерпан. Повторный анализ станет доступен после обновления лимита.';
    }
    if (status === 503) {
        return 'ИИ-распознавание временно недоступно. Попробуйте позже или заполните карточку вручную.';
    }
    if (status === 403) {
        return description || 'Нет права изменять вещи в этом гардеробе.';
    }
    if (status === 404) {
        return description || 'Вещь не найдена или недоступна.';
    }
    return description || 'Не удалось запустить анализ. Попробуйте ещё раз.';
};

/**
 * Запуск повторного ИИ-анализа вещи (item-reanalysis-dialog, FR-2/FR-3).
 *
 * Собирает решение пользователя до траты квоты: по каким фото смотреть, что сделать с новым
 * кадром и что подсказать модели. Раньше повтор запускался одним пунктом меню — молча, сразу
 * и всегда по уже прикреплённым фотографиям.
 */
const ItemReanalysisDialog: React.FC<ItemReanalysisDialogProps> = ({
    open, onClose, itemId, itemPhotos, isOwnWardrobe, onStarted,
}) => {
    const hasCurrentPhotos = itemPhotos.length > 0;
    const [source, setSource] = useState<PhotoSource>(hasCurrentPhotos ? 'current' : 'new');
    const [photoMode, setPhotoMode] = useState<ReanalysisPhotoMode>('APPEND');
    const [comment, setComment] = useState('');
    const [uploadedKey, setUploadedKey] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const previewUrlRef = useRef<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const revokePreview = useCallback(() => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setPreviewUrl(null);
    }, []);

    // Закрытие сбрасывает форму: следующий запуск начинается с чистого решения, а не с
    // остатков предыдущего (в том числе с уже загруженного кадра).
    useEffect(() => {
        if (open) return;
        revokePreview();
        setSource(hasCurrentPhotos ? 'current' : 'new');
        setPhotoMode('APPEND');
        setComment('');
        setUploadedKey(null);
        setUploading(false);
        setStarting(false);
        setError(null);
    }, [open, hasCurrentPhotos, revokePreview]);

    useEffect(() => revokePreview, [revokePreview]);

    const handleFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (files.length === 0) return;
        // Формат и размер проверяем до отправки теми же правилами, что и остальные загрузки:
        // сервер отверг бы файл так же, но ответ нужен сразу и словами.
        const {accepted, rejected} = splitAcceptedImages(files.slice(0, 1));
        if (rejected.length > 0) {
            setError(`${rejected[0].file.name}: ${rejected[0].reason}`);
            return;
        }
        const file = accepted[0];
        if (!file) return;

        setError(null);
        revokePreview();
        const objectUrl = URL.createObjectURL(file);
        previewUrlRef.current = objectUrl;
        setPreviewUrl(objectUrl);
        setUploadedKey(null);
        setUploading(true);
        try {
            const api = await createAxiosInstance();
            const formData = new FormData();
            formData.append('file', file, file.name || `reanalysis-${uniqueFileId()}.jpg`);
            const res = await api.post<{key: string}>('/clothing/images', formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            });
            setUploadedKey(res.data.key);
        } catch (err: unknown) {
            const e = err as {response?: {data?: {description?: string}}};
            setError(e?.response?.data?.description || 'Не удалось загрузить фото');
            revokePreview();
        } finally {
            setUploading(false);
        }
    }, [revokePreview]);

    const dropNewPhoto = useCallback(() => {
        revokePreview();
        setUploadedKey(null);
    }, [revokePreview]);

    const submit = useCallback(async () => {
        setError(null);
        const photoKeys = source === 'current' ? itemPhotos : (uploadedKey ? [uploadedKey] : []);
        if (photoKeys.length === 0) {
            setError(source === 'current'
                ? 'У вещи нет фотографий — загрузите новое фото.'
                : 'Дождитесь загрузки фото.');
            return;
        }
        setStarting(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<{id: string}>('/ai-recognition', {
                photoKeys,
                sourceItemId: itemId,
                description: comment.trim() || undefined,
                photoMode: source === 'new' && isOwnWardrobe ? photoMode : 'KEEP',
            });
            onStarted(res.data.id);
            onClose();
        } catch (err: unknown) {
            setError(startErrorText(err));
        } finally {
            setStarting(false);
        }
    }, [source, itemPhotos, uploadedKey, itemId, comment, isOwnWardrobe, photoMode, onStarted, onClose]);

    const submitDisabled = starting || uploading || (source === 'new' && !uploadedKey);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            busy={starting}
            title="Повторный ИИ-анализ"
            icon={<SparklesIcon className="w-5 h-5 text-primary-500"/>}
            data-testid="item-reanalysis-dialog"
            panelClassName="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto outline-none"
            footer={
                <>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={starting}
                        data-testid="item-reanalysis-cancel"
                        className="btn-secondary disabled:opacity-50"
                    >
                        Отмена
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={submitDisabled}
                        data-testid="item-reanalysis-submit"
                        className="btn-primary disabled:opacity-50"
                    >
                        {starting ? 'Запускаем…' : 'Запустить анализ'}
                    </button>
                </>
            }
        >
            <div className="space-y-4">
                {error && (
                    <p
                        role="alert"
                        data-testid="item-reanalysis-error"
                        className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm"
                    >
                        {error}
                    </p>
                )}

                <fieldset className="space-y-2">
                    <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Фото для анализа
                    </legend>
                    <label className={optionClass(source === 'current', !hasCurrentPhotos)}>
                        <input
                            type="radio"
                            name="reanalysis-source"
                            value="current"
                            checked={source === 'current'}
                            disabled={!hasCurrentPhotos}
                            onChange={() => setSource('current')}
                            data-testid="item-reanalysis-source-current"
                            className="mt-1"
                        />
                        <span className="text-sm">
                            <span className="block text-gray-900 dark:text-gray-100">Текущие фото вещи</span>
                            <span className="block text-gray-500 dark:text-gray-400">
                                {hasCurrentPhotos
                                    ? `Снимков в карточке: ${itemPhotos.length}`
                                    : 'У вещи пока нет фотографий'}
                            </span>
                        </span>
                    </label>
                    <label className={optionClass(source === 'new')}>
                        <input
                            type="radio"
                            name="reanalysis-source"
                            value="new"
                            checked={source === 'new'}
                            onChange={() => setSource('new')}
                            data-testid="item-reanalysis-source-new"
                            className="mt-1"
                        />
                        <span className="text-sm">
                            <span className="block text-gray-900 dark:text-gray-100">Загрузить новое фото</span>
                            <span className="block text-gray-500 dark:text-gray-400">
                                Сфотографируйте вещь как она выглядит сейчас
                            </span>
                        </span>
                    </label>
                </fieldset>

                {source === 'new' && (
                    <div className="space-y-3" data-testid="item-reanalysis-upload">
                        <div className="flex flex-wrap items-center gap-3">
                            {(previewUrl || uploadedKey) ? (
                                <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    {uploadedKey ? (
                                        <AuthImage imageKey={uploadedKey} alt="" variant="thumbnail" className="w-full h-full object-cover"/>
                                    ) : (
                                        <img src={previewUrl ?? ''} alt="" className="w-full h-full object-cover"/>
                                    )}
                                    {uploading && (
                                        <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-xs text-white">
                                            Загрузка…
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={dropNewPhoto}
                                        aria-label="Убрать фото"
                                        data-testid="item-reanalysis-photo-remove"
                                        className="absolute top-0.5 right-0.5 w-8 h-8 flex items-center justify-center rounded-md
                                                   bg-black/60 text-white hover:bg-black/80 transition-colors duration-fast"
                                    >
                                        <XIcon className="w-4 h-4"/>
                                    </button>
                                </div>
                            ) : (
                                <label
                                    className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600
                                               flex flex-col items-center justify-center gap-1 cursor-pointer
                                               hover:border-primary-500 dark:hover:border-primary-400 transition-colors duration-fast"
                                >
                                    <PlusIcon className="w-7 h-7 text-gray-400" aria-hidden="true"/>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">Выбрать</span>
                                    <input
                                        type="file"
                                        accept={IMAGE_ACCEPT}
                                        className="hidden"
                                        onChange={handleFile}
                                        data-testid="item-reanalysis-file"
                                    />
                                </label>
                            )}
                            <p className="text-xs text-gray-500 dark:text-gray-400 flex-1 min-w-[10rem]">
                                Один снимок, JPEG/PNG/WebP/GIF до 10 МБ. Снимайте вещь целиком при дневном свете —
                                модель оценивает в том числе состояние и износ.
                            </p>
                        </div>

                        {isOwnWardrobe ? (
                            <fieldset className="space-y-2">
                                <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Что сделать с новым фото
                                </legend>
                                {PHOTO_FATE_OPTIONS.map(opt => (
                                    <label key={opt.value} className={optionClass(photoMode === opt.value)}>
                                        <input
                                            type="radio"
                                            name="reanalysis-photo-mode"
                                            value={opt.value}
                                            checked={photoMode === opt.value}
                                            onChange={() => setPhotoMode(opt.value)}
                                            data-testid={`item-reanalysis-mode-${opt.value.toLowerCase()}`}
                                            className="mt-1"
                                        />
                                        <span className="text-sm">
                                            <span className="block text-gray-900 dark:text-gray-100">{opt.label}</span>
                                            <span className="block text-gray-500 dark:text-gray-400">{opt.hint}</span>
                                        </span>
                                    </label>
                                ))}
                            </fieldset>
                        ) : (
                            <p data-testid="item-reanalysis-shared-note" className="text-xs text-gray-500 dark:text-gray-400">
                                В чужом гардеробе новое фото используется только для анализа: снимок остаётся
                                в вашем пространстве, и владелец гардероба его не увидит.
                            </p>
                        )}
                    </div>
                )}

                <div>
                    <label htmlFor="reanalysis-comment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Подсказка модели (по желанию)
                    </label>
                    <textarea
                        id="reanalysis-comment"
                        value={comment}
                        onChange={e => setComment(e.target.value.slice(0, MAX_COMMENT))}
                        rows={3}
                        maxLength={MAX_COMMENT}
                        data-testid="item-reanalysis-comment"
                        className="input-field w-full"
                        placeholder="Например: на рукаве появилась потёртость, материал — замша, а не нубук"
                    />
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                    ИИ <strong>предложит</strong> новые значения полей: карточка изменится только после того,
                    как вы проверите результат и подтвердите его — любое поле можно поправить. Запуск
                    расходует одну единицу квоты ИИ-запросов.
                </p>
            </div>
        </Dialog>
    );
};

export default ItemReanalysisDialog;
